import { useIntl } from "react-intl";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import { useMemo, useState } from "react";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import getColumns from "./columns";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import { TableHeaderFilter } from "@lib/types/Api";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { type TableProps } from "antd";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import { SharedContextProps } from "@lib/types";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  apiGetCustomerVoipConfigs,
  apiCreateCustomerVoipConfig,
  apiUpdateCustomerVoipConfig,
  apiDeleteCustomerVoipConfig,
} from "@lib/api/voip";
import { CustomerVoipConfig } from "@lib/types/Voip";
import { CustomerVoipConfigDrawer } from "@lib/components/PageComponents/CustomerVoipConfig";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const SharedCustomerVoipConfigs: React.FC<SharedContextProps> = ({
  customTitle,
  withoutContainer = false,
  pageIsActive = true,
  minusHeight = 0,
  tableProps,
  height,
  queryParams,
  detailContext,
  customerId,
}) => {
  const navigate = useNavigate();
  const { messages } = useIntl();
  const [selected, setSelected] = useState<CustomerVoipConfig | null>(null);
  const [openModal, setModalOpen] = useState(false);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const handleSelect = (item: CustomerVoipConfig) => {
    setSelected(item);
    toggle(true);
  };

  const handleCreate = () => {
    setSelected(null);
    toggle(true);
  };

  const initialFilters = [
    ...(customerId ? [`customerId||$eq||${customerId}`] : []),
  ];

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
    join: ["customer"],
    filter: initialFilters,
    ...queryParams,
  });

  const queryKey = ["customer-voip-configs", JSON.stringify(params)];

  const configsQuery = useQuery({
    queryKey,
    queryFn: () => apiGetCustomerVoipConfigs(params),
    enabled: !!params && !!pageIsActive,
  });

  const { data, isLoading, isFetching, refetch } = configsQuery;
  const apiData = data?.data?.data;

  const createMutation = useMutation({
    mutationFn: apiCreateCustomerVoipConfig,
    onSuccess() {
      toast.success(
        (messages["common.createdMessage"] as string).replace(
          "{name}",
          messages["sidebar.customerVoipConfigs"] as string,
        ),
      );
      refetch();
      toggle(false);
    },
    onError() {
      toast.error(messages["action.error"] as string);
    },
  });

  const updateMutation = useMutation({
    mutationFn: apiUpdateCustomerVoipConfig,
    onSuccess() {
      toast.success(
        (messages["common.updatedMessage"] as string).replace(
          "{name}",
          messages["sidebar.customerVoipConfigs"] as string,
        ),
      );
      refetch();
      toggle(false);
    },
    onError() {
      toast.error(messages["action.error"] as string);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: apiDeleteCustomerVoipConfig,
    onSuccess() {
      toast.success(
        (messages["common.deletedMessage"] as string).replace(
          "{name}",
          messages["sidebar.customerVoipConfigs"] as string,
        ),
      );
      refetch();
    },
    onError() {
      toast.error(messages["action.error"] as string);
    },
  });

  const handleDelete = (item: CustomerVoipConfig) => {
    deleteMutation.mutate(item);
  };

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ ...params, page, limit });
    }
  };

  const onChange: TableProps<CustomerVoipConfig>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        navigate,
        apiData,
        params,
        messages,
        columnsConfig: tableProps?.columnsConfig,
        handleSelect,
        handleDelete,
        detailContext,
      }),
    [
      navigate,
      apiData,
      params,
      tableProps?.columnsConfig,
      messages,
      handleSelect,
      handleDelete,
      detailContext,
    ],
  );

  const hiddenFilters = useMemo(() => {
    if (!!detailContext) {
      return ["customerId"];
    }
    return [];
  }, [detailContext]);

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter((f) => !hiddenFilters.includes(f.key)) as TableHeaderFilter[];
  }, [params?.filter, columns, hiddenFilters]);

  const title =
    customTitle || (messages["sidebar.customerVoipConfigs"] as string);

  return (
    <AppsContainer
      fullView={true}
      title={title}
      noContainer={withoutContainer}
      minusHeight={minusHeight}
      cardProps={{
        borderFull: true,
        coloredHeader: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={configsQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
          tableProps={{ onChange, columns, ...tableProps }}
          plusProps={{
            loading: isLoading,
            disabled: isLoading,
            onClick: handleCreate,
          }}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          columns={columns}
          rowKey={"id"}
          loading={isLoading || isFetching}
          onChange={onChange}
          scroll={{ y: height }}
          minusHeight={minusHeight}
          {...tableProps}
        />
      </AppsContent>

      <CustomerVoipConfigDrawer
        open={openModal}
        onClose={() => toggle(false)}
        item={selected}
        createMutation={createMutation}
        updateMutation={updateMutation}
      />
    </AppsContainer>
  );
};

export default SharedCustomerVoipConfigs;
